import { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Divider,
  Grid,
  Avatar,
  Button,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import apiService from "../../../service/Api";

interface QuoteResponse {
  id: number;
  contact?: {
    id: number;
  };
  deceased?: {
    first_name: string;
    surname: string;
    nickname?: string;
    date_of_birth?: string;
    date_of_death?: string;
  } | null;
  event_datetime: string;
  venue_name: string;
  venue_latitude: string;
  venue_longitude: string;
  created_at: string;
}

const QuoteRequestView = () => {
  const [quote, setQuote] = useState<QuoteResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const response = await apiService.get<QuoteResponse>(
          `/api/bookings/quote-request/${id}/`
        );
        setQuote(response.data);
      } catch (err) {
        console.error("Failed to fetch quote", err);
      } finally {
        setLoading(false);
      }
    };

    fetchQuote();
  }, [id]);

  if (loading) return <Typography>Loading...</Typography>;
  if (!quote) return <Typography>No quote found.</Typography>;

  // Map backend fields for display
  const eventDate = new Date(quote.event_datetime).toLocaleDateString();
  const eventTime = new Date(quote.event_datetime).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
  const belovedName = quote.deceased
    ? `${quote.deceased.first_name} ${quote.deceased.surname}`
    : "N/A";

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", py: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, gap: 1 }}>
        <Button sx={{ border: 2, borderColor: "black", color: "black" }}>
          Convert to Booking
        </Button>
        <Button sx={{ border: 2, borderColor: "black", color: "black" }}>
          Generate Memorial Pages
        </Button>

        <Button
          onClick={() => {
            navigate("/proposals/proposals-builder", {
              state: {
                fromQuoteRequest: true,
                quoteId: quote.id,
                clientId: quote?.contact?.id,
                deceased: quote.deceased
                  ? {
                      first_name: quote.deceased.first_name,
                      surname: quote.deceased.surname,
                      date_of_birth: quote.deceased.date_of_birth,
                      date_of_death: quote.deceased.date_of_death,
                    }
                  : null,
                event: {
                  datetime: quote.event_datetime,
                  venue_name: quote.venue_name,
                },
              },
            });
          }}
          sx={{ border: 2, borderColor: "black", color: "black" }}
        >
          Create Proposal
        </Button>
      </Box>

      <Card sx={{ borderRadius: 3, overflow: "hidden", boxShadow: 0 }}>
        <Box sx={{ background: "#614338", color: "#fff", p: 3 }}>
          <Typography variant="h5" fontWeight={600}>
            Quote Request Details
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
            Submitted on {new Date(quote.created_at).toLocaleString()}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.6 }}>
            ID: {quote.id}
          </Typography>
        </Box>

        <Box sx={{ p: 4 }}>
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Event Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Typography>
                <strong>Event Date:</strong> {eventDate}
              </Typography>
              <Typography>
                <strong>Start Time:</strong> {eventTime}
              </Typography>
              <Typography>
                <strong>Location:</strong> {quote.venue_name}
              </Typography>
              <Typography>
                <strong>Venue Coordinates:</strong> {quote.venue_latitude},{" "}
                {quote.venue_longitude}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Personalization Details
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Typography sx={{ mb: 2 }}>
                <strong>Beloved Name:</strong> {belovedName}
              </Typography>
              {quote.deceased && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Avatar
                    src={quote.deceased.nickname}
                    alt={belovedName}
                    sx={{ width: 64, height: 64 }}
                  />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Full Name
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {belovedName}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, bgcolor: "#f9fafb", borderRadius: 2, p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Complete Information Summary
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Typography>
                  <strong>Request ID:</strong>
                </Typography>
                <Typography>{quote.id}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography>
                  <strong>Submitted:</strong>
                </Typography>
                <Typography>
                  {new Date(quote.created_at).toLocaleString()}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography>
                  <strong>Event Date:</strong>
                </Typography>
                <Typography>{eventDate}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography>
                  <strong>Event Time:</strong>
                </Typography>
                <Typography>{eventTime}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography>
                  <strong>Location:</strong>
                </Typography>
                <Typography>{quote.venue_name}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography>
                  <strong>Coordinates:</strong>
                </Typography>
                <Typography>
                  {quote.venue_latitude}, {quote.venue_longitude}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default QuoteRequestView;
